/**
 * The section registry shared by the two Notes pickers ("Posting to" and the People filter): which
 * headings exist, in which order, and which rows each shows. Opportunities will be one more entry in
 * `NOTES_SECTIONS` (and a `section: 'opportunities'` on its options); nothing else needs to change.
 */
export const NOTES_SECTIONS = [
  { key: 'account', heading: 'Account' },
  { key: 'people', heading: 'People' },
] as const;

export type NotesSectionKey = (typeof NOTES_SECTIONS)[number]['key'];

export interface SectionableOption {
  key: string;
  label: string;
  section: NotesSectionKey;
  /** Hidden while nothing is typed (find it by searching). The caller decides, e.g. a person with no notes who is not selected. */
  hiddenUntilSearch?: boolean;
}

export interface SectionedOptions<T extends SectionableOption> {
  sections: { key: NotesSectionKey; heading: string; options: T[] }[];
  /** Every shown option in display order (what arrow keys walk). */
  flat: T[];
  /** People tucked away for having no notes; always 0 while a query is typed. */
  hiddenNoNotes: number;
}

/**
 * Filters `options` by `query` (trimmed, case-insensitive, on the label) and groups them by section
 * in registry order; an empty section is dropped. With no query, `hiddenUntilSearch` rows are hidden
 * (and counted).
 */
export function sectionOptions<T extends SectionableOption>(options: T[], query: string): SectionedOptions<T> {
  const q = query.trim().toLowerCase();
  let hiddenNoNotes = 0;
  const shown = options.filter(o => {
    if (q !== '') return o.label.toLowerCase().includes(q);
    if (o.hiddenUntilSearch) {
      hiddenNoNotes += 1;
      return false;
    }
    return true;
  });
  const sections = NOTES_SECTIONS.map(s => ({ key: s.key, heading: s.heading, options: shown.filter(o => o.section === s.key) })).filter(
    s => s.options.length > 0,
  );
  return { sections, flat: sections.flatMap(s => s.options), hiddenNoNotes };
}

/** Footer for the People filter when some people were hidden for having no notes. */
export function noNotesFooter(hidden: number): string {
  return `${hidden} more ${hidden === 1 ? 'person' : 'people'} with no notes. Search to find them.`;
}
